import {
  init,
  browserTracingIntegration,
  viewHierarchyIntegration,
  captureException,
  setUser,
  setTag,
} from "@sentry/browser";
import { PAGE_TYPES } from "../constants/pageTypes";
import { getConsentSnapshot } from "../utils/cookiebotConsentSnapshot";
import { applySentryConsent } from "../utils/applySentryConsent";
import { hasStatisticsConsent } from "../utils/cookiebotConsent";

init({
  dsn: "https://6db92db672e0849772464f0e3d544f0d@o4509294734540800.ingest.de.sentry.io/4509294772879440",
  // TRACING
  integrations: [browserTracingIntegration(), viewHierarchyIntegration()],
  tracesSampleRate: 1.0,
  tracePropagationTargets: [],
  // NOISE FILTERS
  ignoreErrors: [
    "Non-Error promise rejection captured",
    /ResizeObserver loop limit exceeded/,
    /Script error\.?$/,
  ],
  // OTHER
  environment:
    process.env.NODE_ENV === "development" ? "development" : "production",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: false,
  debug: false,
  /**
   * Tags transactions with the page type for filtering in Sentry.
   * @param {object} event - The Sentry transaction event.
   * @returns {object} The modified event.
   */
  beforeSendTransaction(event) {
    const pageType = window.__pageType || PAGE_TYPES.GENERAL;
    return { ...event, tags: { ...event.tags, page_type: pageType } };
  },
  /**
   * Filters sensitive data before sending events to Sentry.
   * @param {object} event - The Sentry event object.
   * @returns {object} The filtered event object.
   */
  beforeSend(event) {
    // Log to console in development for debugging
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.log("📡 Sentry Event:", event);
    }

    // Don't send user IP
    if (event.user && event.user.ip_address) {
      delete event.user.ip_address;
    }
    return event;
  },
});

try {
  setUser({ id: createGuestId() });
  applySentryConsent(getConsentSnapshot());
} catch (error) {
  captureException(error, {
    tags: { sentry: "init_user_consent" },
    extra: {
      context_message:
        "Failed to set guest ID or initial consent state after Sentry init",
    },
  });
}

/**
 * Creates or retrieves a persistent guest ID for anonymous users.
 * @returns {string} The guest ID.
 */
function createGuestId() {
  let guestId = localStorage.getItem("sentryGuestId");
  if (!guestId) {
    try {
      guestId = crypto.randomUUID();
      localStorage.setItem("sentryGuestId", guestId);
    } catch (e) {
      guestId =
        "sentry-guest-" +
        Date.now() +
        "-" +
        Math.random().toString(36).substring(2, 15);
      localStorage.setItem("sentryGuestId", guestId);
    }
  }
  return guestId;
}

/**
 * Initializes Sentry with user context based on consent and user status.
 * @returns {void}
 */
function initializeSentryWithUserContext() {
  applySentryConsent(getConsentSnapshot());

  if (hasStatisticsConsent()) {
    const headerElement = document.getElementById("top");
    let isBusinessUser = false;
    // Logged-in users intentionally reuse the persistent guest UUID rather than
    // their real account ID — keeps Sentry IDs anonymous-by-default.
    const userIdToSet = createGuestId();
    let userType = "guest";

    const hasValidUserId =
      headerElement &&
      headerElement.dataset.userId &&
      headerElement.dataset.userId.trim() !== "";
    if (hasValidUserId) {
      userType = "logged_in";
      const hasValidBusinessUserFlag =
        headerElement.dataset.isBusinessUser &&
        headerElement.dataset.isBusinessUser.trim() !== "";
      if (hasValidBusinessUserFlag) {
        isBusinessUser = headerElement.dataset.isBusinessUser === "1";
      }
    }

    if (userIdToSet) {
      setUser({ id: userIdToSet });
      setTag("user_type", userType);
      setTag("is_business_user", isBusinessUser);
    } else {
      setTag("user_type", "unknown_guest");
    }
  }
}

/**
 * Sets up Sentry user tracking after Cookiebot loads.
 * @returns {void}
 */
function setSentryUser() {
  if (window.Cookiebot) {
    if (window.Cookiebot.consent) {
      initializeSentryWithUserContext();
    }
    ["CookiebotOnLoad", "CookiebotOnAccept", "CookiebotOnDecline"].forEach(
      (eventName) => {
        window.addEventListener(eventName, initializeSentryWithUserContext);
      },
    );
  }
}

try {
  setSentryUser();
} catch (error) {
  captureException(error, {
    tags: { sentry: "set_user" },
    extra: { context_message: "Error trying to set user in Sentry" },
  });
}
